import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import {
	formatNumberField,
	transformToUI,
} from '@atlassian/jira-platform-utils-number/src/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type {
	number_issueFieldNumberReadviewFull_NumberReadView$data as NumberData,
	number_issueFieldNumberReadviewFull_NumberReadView$key as NumberFragment,
} from './__generated__/number_issueFieldNumberReadviewFull_NumberReadView.graphql';
import type { Props, PropsWithoutFragment } from './types.tsx';
import messages from './messages.tsx';

type NumberReadViewInnerProps = PropsWithoutFragment & {
	fieldData: Omit<NumberData, ' $fragmentType' | 'id'> & { id?: string };
	shouldTransformNumber?: boolean;
};

export const NumberReadViewInner = ({
	fieldData,
	isSingleRowHeightEnabled = false,
	shouldTransformNumber = true,
}: NumberReadViewInnerProps): React.JSX.Element => {
	const { formatNumber, formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const locale = useLocale();
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const hoverPopoverRefFactory = isSingleRowHeightEnabled
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useHoverPopoverContentRef(fieldData.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;

	const { number, formatConfig } = fieldData;
	const tranformedNumber =
		shouldTransformNumber && fg('frontier-currency-field-fix-percentage-storage')
			? transformToUI(number, formatConfig?.formatStyle)
			: number;
	const numberContent =
		formatConfig && formatConfig.formatStyle && fg('frontier-currency-field-d1')
			? formatNumberField(tranformedNumber ?? 0, {
					formatStyle: formatConfig.formatStyle,
					formatUnit: formatConfig.formatUnit,
					locale,
				})
			: formatNumber(number ?? 0);
	const children =
		number === null || number === undefined ? (
			<EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : undefined} />
		) : (
			numberContent
		);

	if (isSingleRowHeightEnabled) {
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: numberContent,
			});
		}
		return (
			<Text
				testId="issue-field-number-readview-full.ui.number.text"
				ref={hoverPopoverRef}
				maxLines={1}
			>
				{children}
			</Text>
		);
	}

	return <span data-testid="issue-field-number-readview-full.ui.number.span">{children}</span>;
};

export const NumberReadView = ({
	fragmentRef,
	isSingleRowHeightEnabled = false,
}: Props): React.JSX.Element => {
	const fieldData = useFragment<NumberFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment number_issueFieldNumberReadviewFull_NumberReadView on JiraNumberField
			@argumentDefinitions(
				isNumberFieldConfigurableEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-number-field-configuration-enabled.relayprovider"
				}
			) {
				id
				number
				formatConfig @include(if: $isNumberFieldConfigurableEnabled) {
					formatStyle
					formatUnit
				}
			}
		`,
		fragmentRef,
	);

	return (
		<NumberReadViewInner
			fieldData={fieldData}
			isSingleRowHeightEnabled={isSingleRowHeightEnabled}
		/>
	);
};
